export const MAX_MEANINGFUL_YOY_PCT = 500;

interface MonthEmissions {
  currentEmissionsKg: number;
  previousEmissionsKg?: number | null;
}

export interface YoySummary {
  /** The API returned a prior-year series (at least one non-null previousEmissionsKg). */
  hasPrevious: boolean;
  /** Year-over-year percentage, or null when it can't be computed. */
  pct: number | null;
  /** pct exists AND its magnitude is within the meaningful cap (baseline not negligible). */
  meaningful: boolean;
}

/**
 * Summarize year-over-year change from a monthly emissions series.
 * A near-zero prior-year baseline (e.g. a few kg from partial history) makes the
 * raw percentage explode into a meaningless value, so anything beyond
 * MAX_MEANINGFUL_YOY_PCT is treated as "not comparable".
 */
export const computeYoy = (
  rows: MonthEmissions[],
  maxPct: number = MAX_MEANINGFUL_YOY_PCT
): YoySummary => {
  const hasPrevious = rows.some((r) => r.previousEmissionsKg != null);
  const current = rows.reduce((s, r) => s + r.currentEmissionsKg, 0);
  const previous = rows.reduce((s, r) => s + (r.previousEmissionsKg ?? 0), 0);
  const pct =
    hasPrevious && previous > 0 ? ((current - previous) / previous) * 100 : null;
  const meaningful = pct != null && Math.abs(pct) <= maxPct;
  return { hasPrevious, pct, meaningful };
};
